/**
 * KML（`doc.kml`）の地点読み込み。
 *
 * 原典 GoalView は会場ごとの `doc.kml` を地点マスタとして持ち、
 * `$_MARK` で共有された「名前」をこれで座標に解決する（`GoalView.cpp:1606`）。
 * 名前が引けないと地点が消えるので、次世代版でも必須の入力になる。
 *
 * ブラウザ・Node の両方で動かすため DOMParser は使わず、
 * `<Placemark>` 単位で切り出して必要な要素だけ取り出す。
 * 地点マスタとして使うぶんには十分で、依存も増えない。
 */

import type { MarkPoint } from './types.ts';
import { toUtm } from './utm.ts';

export interface KmlPlacemark {
  name: string;
  lat: number;
  lon: number;
  /** KML の第3要素。指定が無ければ 0 */
  alt: number;
}

/** XML の実体参照を戻す（地点名に `&` などが入りうる） */
function unescapeXml(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');
}

/**
 * KML から地点を取り出す。
 *
 * `<Point>` を持つ `<Placemark>` だけを対象にする。
 * 経路（LineString）やポリゴンは地点マスタではないので拾わない。
 */
export function parseKmlPlacemarks(text: string): KmlPlacemark[] {
  const out: KmlPlacemark[] = [];

  // <Placemark> ごとに切る。名前と座標の取り違えを防ぐため、必ず塊で処理する
  for (const block of text.split(/<Placemark\b/i).slice(1)) {
    const body = block.slice(0, block.search(/<\/Placemark>/i) + 1 || undefined);

    // <Point> を持たないものは地点ではない
    if (!/<Point\b/i.test(body)) continue;

    const nameMatch = /<name\b[^>]*>([\s\S]*?)<\/name>/i.exec(body);
    const coordMatch = /<coordinates\b[^>]*>([\s\S]*?)<\/coordinates>/i.exec(body);
    if (!coordMatch) continue;

    // 経度,緯度[,高度]
    const parts = coordMatch[1].trim().split(/\s+/)[0].split(',');
    const lon = Number(parts[0]);
    const lat = Number(parts[1]);
    const alt = Number(parts[2] ?? 0);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;

    out.push({
      name: nameMatch ? unescapeXml(nameMatch[1]).trim() : '',
      lat,
      lon,
      alt: Number.isFinite(alt) ? alt : 0,
    });
  }

  return out;
}

/** KML の地点を共通データモデルへ変換する */
export function kmlToMarkPoints(placemarks: KmlPlacemark[]): MarkPoint[] {
  return placemarks.map((p) => ({
    name: p.name,
    lat: p.lat,
    lon: p.lon,
    utm: toUtm(p.lon, p.lat),
    alt: p.alt !== 0 ? p.alt : null,
    radius: null,
  }));
}

/**
 * 地点マスタ（名前 → 座標）を作る。
 *
 * `$_MARK` で受け取った名前を解決するために使う。
 * `resolveMarkNames` の `lookup` にそのまま渡せる形にしてある。
 */
export function buildMarkLookup(
  marks: MarkPoint[],
): (name: string) => { lat: number; lon: number } | undefined {
  const index = new Map<string, { lat: number; lon: number }>();
  for (const m of marks) {
    if (m.name) index.set(m.name, { lat: m.lat, lon: m.lon });
  }
  return (name: string) => index.get(name);
}
